import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { generateStandardCacheKey } from "./cache_helpers";
import { getCachePolicy, isCacheEnabled, mergePolicyWithOverrides, resolvePolicyDebug } from "@/lib/cache-policy";
import { scheduleCacheInvalidation, shouldInvalidateForMutation } from "@/lib/cache-invalidation";
import { API_BASE_URL } from "@/lib/config/api-base-url";

// Re-exported for existing call sites (`import { API_BASE_URL } from
// "@/lib/api_utils"`) — see lib/config/api-base-url.ts for what this
// actually resolves to and why (local dev-stacks tunnel auto-detection).
export { API_BASE_URL };

// VAPT 2026-08 #15: auth is now carried by HttpOnly access/refresh cookies rather
// than an Authorization: Bearer header. Send credentials on every request so the
// cookies ride along (dashboard.airbills.co -> pro.airbills.co is cross-origin but
// same-site; the backend sets CORS_ALLOW_CREDENTIALS=true and reflects localhost).
axios.defaults.withCredentials = true;

// Create axios instance with interceptors for token expiration handling
const axiosInstance = axios.create({ withCredentials: true });

/**
 * Read a non-HttpOnly cookie value from document.cookie. Only the CSRF cookie
 * (`csrftoken`) and the readable `impersonation_active` marker are exposed this
 * way; the access/refresh JWTs are HttpOnly and intentionally NOT readable here.
 */
export const readCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
};

// Centralized token expiration handler
const handleTokenExpiration = () => {
  console.log("Token expired detected. Logging out user...");

  // Dynamically import to avoid circular dependency
  import("../store/authStore").then(({ useAuthStore }) => {
    // Clear auth data and redirect to sign-in
    useAuthStore.getState().logout();
  }).catch((err) => {
    console.error("Error importing authStore:", err);
    // Fallback: Clear storage and redirect manually
    if (typeof window !== "undefined") {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/sign-in";
    }
  });
};

// VAPT 2026-08 #9: access tokens are short-lived (30 min), so requests can 401
// mid-session. A single-flight refresh dedupes concurrent 401s into one
// /token/refresh/ call and updates localStorage with the rotated tokens.
let refreshInFlight: Promise<string | null> | null = null;

// VAPT #15: the refresh token is an HttpOnly cookie now, so there is no
// localStorage gate. We hit /token/refresh/ with credentials (cookie carries
// the refresh token); the backend rotates BOTH cookies. The returned string is
// only used as a success signal - the retried request replays with credentials
// and needs no Authorization header.
const attemptTokenRefresh = async (): Promise<string | null> => {
  if (typeof window === "undefined") return null;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        // Dynamic import avoids a static circular dependency with api/auth.
        const { refreshToken } = await import("../api/auth");
        await refreshToken();
        return "ok";
      } catch {
        return null;
      }
    })();
    refreshInFlight.finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

const isTokenErrorResponse = (
  status: number | undefined,
  detail: string | undefined
): boolean => {
  const isTokenExpired = status === 403 && detail === "Token expired";
  const isTokenInvalid =
    status === 401 ||
    (!!detail &&
      (detail.includes("Given token not valid") ||
        detail.includes("Token is invalid") ||
        detail.includes("Token is blacklisted") ||
        detail.includes("Token has expired") ||
        // SessionTokenAuthentication (backend) surfaces expiry as
        // "Token error: Token is expired" / "Invalid token: ...". Match these
        // so a stale cookie/JWT triggers the silent refresh on ANY endpoint,
        // not just the ones that return 401.
        detail.includes("Token is expired") ||
        detail.includes("Token error") ||
        detail.includes("Invalid token")));
  return isTokenExpired || isTokenInvalid;
};

// Global post-mutation cache invalidation. See lib/cache-invalidation.ts for
// the strict URL guard + debounced multi-layer clear.
const maybeInvalidateAfterMutation = (config?: AxiosRequestConfig): void => {
  if (shouldInvalidateForMutation(config?.method, config?.url)) {
    scheduleCacheInvalidation();
  }
};

// Response interceptor: on a token error, try one silent refresh + retry before
// giving up and logging the user out. On success, auto-invalidate caches after
// any write.
const onResponseFulfilled = (response: AxiosResponse): AxiosResponse => {
  maybeInvalidateAfterMutation(response.config);
  return response;
};

const onResponseRejected = async (
  error: AxiosError<{ detail?: string }>
): Promise<AxiosResponse> => {
  const status = error.response?.status;
  const detail = error.response?.data?.detail;
  const original = error.config as
    | (AxiosRequestConfig & { _retried?: boolean })
    | undefined;

  if (isTokenErrorResponse(status, detail)) {
    if (original && !original._retried) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        // VAPT #15: no Bearer header - the rotated cookies authenticate the
        // replay. Replay through axiosInstance regardless of which client
        // issued the original request (both share withCredentials=true), so
        // the retried call also carries the refresh + read-only interceptors.
        original._retried = true;
        return axiosInstance(original);
      }
    }
    handleTokenExpiration();
  }

  return Promise.reject(error);
};

// Register on BOTH the shared instance and the default axios export. Many API
// modules import `axios` directly (see the request-interceptor note above), and
// axios.create() instances do NOT inherit default-instance interceptors - so
// without this, bare-axios calls skip the silent refresh + logout on an expired
// access cookie and hard-fail with a raw 401 (VAPT #15 regression).
axiosInstance.interceptors.response.use(onResponseFulfilled, onResponseRejected);
axios.interceptors.response.use(onResponseFulfilled, onResponseRejected);

// Export the axios instance so other files can use it
export { axiosInstance };

// ---------------------------------------------------------------------------
// Read-only "Login As" impersonation guard.
//
// When staff view the app as a resident (login-as), the JWT carries an
// `impersonation_readonly` claim and the backend rejects every write with a
// 403. These client-side guards fail fast with a friendly toast instead of
// letting Pay Now / ticket buttons surface raw server errors. The backend
// middleware remains the actual security gate.
// ---------------------------------------------------------------------------

const READONLY_SAFE_METHODS = ["get", "head", "options"];
// Writes the read-only session itself still needs.
const READONLY_ALLOWED_WRITE_PATHS = ["/token/refresh", "/admin-verify-code", "/auth/logout"];

// VAPT #15: read-only impersonation is derived from the /me-hydrated identity
// in authStore, not localStorage.token_payload. A dynamic import (cached) avoids
// a static circular dependency with api/auth <-> authStore. This client guard is
// best-effort UX; the backend remains the actual security gate on writes.
let _authStoreRef: typeof import("@/store/authStore").useAuthStore | null = null;
if (typeof window !== "undefined") {
  import("@/store/authStore")
    .then((m) => {
      _authStoreRef = m.useAuthStore;
    })
    .catch(() => {});
}

export const isReadOnlyImpersonation = (): boolean => {
  try {
    return Boolean(_authStoreRef?.getState().token_payload?.impersonation_readonly);
  } catch {
    return false;
  }
};

const shouldBlockReadOnlyWrite = (method?: string, url?: string): boolean => {
  if (!isReadOnlyImpersonation()) return false;
  if (READONLY_SAFE_METHODS.includes((method || "get").toLowerCase())) {
    return false;
  }
  return !READONLY_ALLOWED_WRITE_PATHS.some((path) => (url || "").includes(path));
};

const notifyReadOnlyBlocked = () => {
  import("@/lib/utils/toast")
    .then(({ toast }) => {
      toast.error("Read-only view", {
        description:
          "Actions are disabled while viewing as a resident. Return to your account to make changes.",
      });
    })
    .catch(() => {});
};

const readOnlyRequestInterceptor = (config: any) => {
  if (shouldBlockReadOnlyWrite(config.method, config.url)) {
    notifyReadOnlyBlocked();
    return Promise.reject(
      new axios.Cancel("Blocked: read-only view-as session")
    );
  }
  return config;
};

// Cover both the shared instance and the default axios export (many API
// modules import axios directly). Instances created via axios.create() do
// NOT inherit default-instance interceptors, so both need it.
axiosInstance.interceptors.request.use(readOnlyRequestInterceptor);
axios.interceptors.request.use(readOnlyRequestInterceptor);

// VAPT #15 CSRF double-submit (axios side). The auth helpers add X-CSRFToken via
// getAuthHeaders(), but many modules call axios.post/put/delete directly and rely
// on each call site remembering to pass it. Inject it here for ALL unsafe-method
// axios writes so no direct call site can forget - the axios twin of the
// window.fetch patch below. Safe methods get {} (untouched); an X-CSRFToken the
// caller already set is never overwritten (handles both AxiosHeaders + plain obj).
const csrfRequestInterceptor = (config: any) => {
  const csrf = getAuthHeaders(config.method);
  if (Object.keys(csrf).length > 0) {
    config.headers = config.headers || {};
    for (const [key, value] of Object.entries(csrf)) {
      const alreadySet =
        typeof config.headers.has === "function"
          ? config.headers.has(key)
          : config.headers[key] != null;
      if (alreadySet) continue;
      if (typeof config.headers.set === "function") config.headers.set(key, value);
      else config.headers[key] = value;
    }
  }
  return config;
};
axiosInstance.interceptors.request.use(csrfRequestInterceptor);
axios.interceptors.request.use(csrfRequestInterceptor);

// Many API modules call the default `axios` export directly (bypassing
// axiosInstance). Attach the same post-mutation invalidation there so every
// write auto-refreshes caches regardless of which client made the request.
axios.interceptors.response.use((response) => {
  maybeInvalidateAfterMutation(response.config);
  return response;
});

/**
 * Global fetch interceptor for token expiration handling
 * This monkey-patches the native fetch to intercept all responses
 */
if (typeof window !== "undefined") {
  const originalFetch = window.fetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // Read-only impersonation guard — only our API calls, not third-party
    // fetches (analytics, Next.js internals, etc.).
    const fetchUrl =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input.url;
    const isApiCall =
      !!API_BASE_URL && fetchUrl.startsWith(API_BASE_URL);
    const fetchMethod =
      init?.method || (input instanceof Request ? input.method : "GET");
    if (isApiCall && shouldBlockReadOnlyWrite(fetchMethod, fetchUrl)) {
      notifyReadOnlyBlocked();
      return new Response(
        JSON.stringify({
          success: false,
          error: "Read-only view-as session. Action blocked.",
        }),
        { status: 403, headers: { "Content-Type": "application/json" } }
      );
    }

    // VAPT #15: send the HttpOnly auth cookies on our own API calls. Callers that
    // use raw fetch don't set credentials, so inject it here for API_BASE_URL URLs.
    if (isApiCall) {
      init = { ...(init || {}), credentials: "include" };

      // VAPT #15 CSRF double-submit: the axios helpers add X-CSRFToken via
      // getAuthHeaders(), but raw-fetch callers don't. Inject it here so EVERY
      // cookie-authenticated unsafe request (POST/PUT/PATCH/DELETE) echoes the
      // csrftoken cookie - otherwise the backend rejects with "CSRF Failed:
      // CSRF token missing." Safe methods return {} and are left untouched; an
      // explicit X-CSRFToken already on the request is never overwritten.
      const csrfHeaders = getAuthHeaders(fetchMethod);
      if (Object.keys(csrfHeaders).length > 0) {
        const merged = new Headers(
          init.headers ?? (input instanceof Request ? input.headers : undefined)
        );
        for (const [key, value] of Object.entries(csrfHeaders)) {
          if (!merged.has(key)) merged.set(key, value);
        }
        init.headers = merged;
      }
    }

    let response: Response;
    try {
      response = await originalFetch(input, init);
    } catch (err) {
      // Network failure (offline, DNS, CORS, aborted) — re-throw as-is
      throw err;
    }

    // Check if it's a 401 Unauthorized or 403 Forbidden error
    if (response.status === 401 || response.status === 403) {
      try {
        const clonedResponse = response.clone();
        const data = await clonedResponse.json();

        const detail = data.detail || "";
        // Reuse the shared matcher so the fetch path and the axios interceptor
        // agree on what counts as a token error (was a divergent inline copy).
        const isTokenError = isTokenErrorResponse(response.status, detail);

        if (isTokenError) {
          // VAPT #9/#15: attempt one silent cookie refresh + replay before
          // logging out. The rotated cookies authenticate the replay - no
          // Authorization header is set.
          const alreadyRetried = Boolean((init as any)?.__retried);
          if (isApiCall && !alreadyRetried) {
            const refreshed = await attemptTokenRefresh();
            if (refreshed) {
              const retryInit: RequestInit = {
                ...(init || {}),
                credentials: "include",
              };
              (retryInit as any).__retried = true;
              return await originalFetch(input, retryInit);
            }
          }
          handleTokenExpiration();
        }
      } catch (err) {
        // Not JSON — ignore, not all 401/403 responses are JSON
      }
    }

    return response;
  };
}

// Helper function to build API URLs with fallback
const buildApiUrl = (endpoint: string): string => {
  // If endpoint is already an absolute URL (starts with http:// or https://), return as-is
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }

  // If API_BASE_URL is not set or empty, use relative URL
  if (!API_BASE_URL || API_BASE_URL === "") {
    return endpoint;
  }
  // Remove trailing slash from API_BASE_URL and leading slash from endpoint to avoid double slashes
  const cleanBaseUrl = API_BASE_URL.replace(/\/+$/, '');
  const cleanEndpoint = endpoint.replace(/^\/+/, '');
  return `${cleanBaseUrl}/${cleanEndpoint}`;
};

// --- Cache Configuration ---
export interface CacheConfig {
  /** Cache duration in minutes (default: 20) */
  duration?: number;
  /** Force refresh the cache regardless of expiry */
  force?: boolean;
  /** Custom cache key (if not provided, will be generated from URL and params) */
  key?: string;
  /** Storage type to use for caching (default: 'localStorage') */
  storage?: "localStorage" | "sessionStorage" | "memory";
  /** Ignore cache and fetch fresh data, then update cache with new data */
  ignoreCache?: boolean;
}

interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

type StorageType = "localStorage" | "sessionStorage" | "memory";

class ApiCache {
  private memoryCache = new Map<string, CacheEntry>();
  private readonly DEFAULT_DURATION_MINUTES = 20;
  private readonly CACHE_PREFIX = "api_cache_";
  private readonly defaultStorage: StorageType = "sessionStorage";

  /**
   * Determines the best available storage option
   */
  private getAvailableStorage(
    preferredStorage: StorageType = this.defaultStorage
  ): StorageType {
    if (typeof window === "undefined") return "memory";

    try {
      if (preferredStorage === "localStorage" && window.localStorage) {
        // Test if localStorage is actually writable
        const testKey = "__test_storage__";
        window.localStorage.setItem(testKey, "test");
        window.localStorage.removeItem(testKey);
        return "localStorage";
      }

      if (preferredStorage === "sessionStorage" && window.sessionStorage) {
        // Test if sessionStorage is actually writable
        const testKey = "__test_storage__";
        window.sessionStorage.setItem(testKey, "test");
        window.sessionStorage.removeItem(testKey);
        return "sessionStorage";
      }

      // Fallback to localStorage if sessionStorage was preferred but not available
      if (preferredStorage === "sessionStorage" && window.localStorage) {
        const testKey = "__test_storage__";
        window.localStorage.setItem(testKey, "test");
        window.localStorage.removeItem(testKey);
        return "localStorage";
      }

      // Fallback to sessionStorage if localStorage was preferred but not available
      if (preferredStorage === "localStorage" && window.sessionStorage) {
        const testKey = "__test_storage__";
        window.sessionStorage.setItem(testKey, "test");
        window.sessionStorage.removeItem(testKey);
        return "sessionStorage";
      }
    } catch (e) {
      console.warn("Storage not available, falling back to memory cache:", e);
    }

    return "memory";
  }

  /**
   * Gets the storage interface based on storage type
   */
  private getStorage(storageType: StorageType): Storage | null {
    if (typeof window === "undefined") return null;

    switch (storageType) {
      case "localStorage":
        return window.localStorage;
      case "sessionStorage":
        return window.sessionStorage;
      default:
        return null;
    }
  }

  /**
   * Generates a cache key from URL and parameters
   * Uses the standardized cache key generation function
   */
  private generateKey(url: string, params?: any, headers?: any): string {
    return generateStandardCacheKey(url, params, headers);
  }

  /**
   * Gets the full storage key with prefix
   */
  private getStorageKey(key: string): string {
    return `${this.CACHE_PREFIX}${key}`;
  }

  /**
   * Gets data from cache if valid
   */
  get<T>(
    key: string,
    storageType: StorageType = this.defaultStorage
  ): T | null {
    const actualStorageType = this.getAvailableStorage(storageType);

    try {
      let entry: CacheEntry<T> | null = null;

      if (actualStorageType === "memory") {
        entry = this.memoryCache.get(key) || null;
      } else {
        const storage = this.getStorage(actualStorageType);
        if (storage) {
          const stored = storage.getItem(this.getStorageKey(key));
          if (stored) {
            entry = JSON.parse(stored);
          }
        }
      }

      if (!entry) return null;

      const now = Date.now();
      if (now > entry.expiresAt) {
        this.delete(key, actualStorageType);
        return null;
      }

      return entry.data;
    } catch (error) {
      console.warn(`Error reading from ${actualStorageType} cache:`, error);
      // Try to delete the corrupted entry
      this.delete(key, actualStorageType);
      return null;
    }
  }

  /**
   * Sets data in cache with expiration
   */
  set<T>(
    key: string,
    data: T,
    durationMinutes: number = this.DEFAULT_DURATION_MINUTES,
    storageType: StorageType = this.defaultStorage
  ): void {
    const actualStorageType = this.getAvailableStorage(storageType);
    const now = Date.now();
    const expiresAt = now + durationMinutes * 60 * 1000;

    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      expiresAt,
    };

    try {
      if (actualStorageType === "memory") {
        this.memoryCache.set(key, entry);
      } else {
        const storage = this.getStorage(actualStorageType);
        if (storage) {
          storage.setItem(this.getStorageKey(key), JSON.stringify(entry));
        }
      }
    } catch (error) {
      console.warn(
        `Error writing to ${actualStorageType} cache, falling back to memory:`,
        error
      );
      // Fallback to memory cache if storage fails
      this.memoryCache.set(key, entry);
    }
  }

  /**
   * Removes specific entry from cache
   */
  delete(key: string, storageType: StorageType = this.defaultStorage): void {
    const actualStorageType = this.getAvailableStorage(storageType);

    try {
      if (actualStorageType === "memory") {
        this.memoryCache.delete(key);
      } else {
        const storage = this.getStorage(actualStorageType);
        if (storage) {
          storage.removeItem(this.getStorageKey(key));
        }
      }
    } catch (error) {
      console.warn(`Error deleting from ${actualStorageType} cache:`, error);
    }

    // Also remove from memory cache as a fallback
    this.memoryCache.delete(key);
  }

  /**
   * Clears all cache entries
   */
  clear(storageType: StorageType = this.defaultStorage): void {
    const actualStorageType = this.getAvailableStorage(storageType);

    try {
      if (actualStorageType === "memory") {
        this.memoryCache.clear();
      } else {
        const storage = this.getStorage(actualStorageType);
        if (storage) {
          // Remove all keys that start with our cache prefix
          const keysToRemove: string[] = [];
          for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (key && key.startsWith(this.CACHE_PREFIX)) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach((key) => storage.removeItem(key));
        }
      }
    } catch (error) {
      console.warn(`Error clearing ${actualStorageType} cache:`, error);
    }

    // Also clear memory cache
    this.memoryCache.clear();
  }

  /**
   * Removes expired entries from cache
   */
  cleanup(storageType: StorageType = this.defaultStorage): void {
    const actualStorageType = this.getAvailableStorage(storageType);
    const now = Date.now();

    try {
      if (actualStorageType === "memory") {
        for (const [key, entry] of this.memoryCache.entries()) {
          if (now > entry.expiresAt) {
            this.memoryCache.delete(key);
          }
        }
      } else {
        const storage = this.getStorage(actualStorageType);
        if (storage) {
          const keysToRemove: string[] = [];

          for (let i = 0; i < storage.length; i++) {
            const storageKey = storage.key(i);
            if (storageKey && storageKey.startsWith(this.CACHE_PREFIX)) {
              try {
                const stored = storage.getItem(storageKey);
                if (stored) {
                  const entry: CacheEntry = JSON.parse(stored);
                  if (now > entry.expiresAt) {
                    keysToRemove.push(storageKey);
                  }
                }
              } catch (error) {
                // If we can't parse the entry, it's corrupted, so remove it
                keysToRemove.push(storageKey);
              }
            }
          }

          keysToRemove.forEach((key) => storage.removeItem(key));
        }
      }
    } catch (error) {
      console.warn(`Error during ${actualStorageType} cache cleanup:`, error);
    }

    // Also cleanup memory cache
    for (const [key, entry] of this.memoryCache.entries()) {
      if (now > entry.expiresAt) {
        this.memoryCache.delete(key);
      }
    }
  }

  /**
   * Gets cache statistics
   */
  getStats(storageType: StorageType = this.defaultStorage) {
    const actualStorageType = this.getAvailableStorage(storageType);
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    let totalEntries = 0;

    try {
      if (actualStorageType === "memory") {
        totalEntries = this.memoryCache.size;
        for (const entry of this.memoryCache.values()) {
          if (now > entry.expiresAt) {
            expiredEntries++;
          } else {
            validEntries++;
          }
        }
      } else {
        const storage = this.getStorage(actualStorageType);
        if (storage) {
          for (let i = 0; i < storage.length; i++) {
            const storageKey = storage.key(i);
            if (storageKey && storageKey.startsWith(this.CACHE_PREFIX)) {
              totalEntries++;
              try {
                const stored = storage.getItem(storageKey);
                if (stored) {
                  const entry: CacheEntry = JSON.parse(stored);
                  if (now > entry.expiresAt) {
                    expiredEntries++;
                  } else {
                    validEntries++;
                  }
                }
              } catch (error) {
                expiredEntries++; // Count corrupted entries as expired
              }
            }
          }
        }
      }
    } catch (error) {
      console.warn(`Error getting ${actualStorageType} cache stats:`, error);
    }

    return {
      storageType: actualStorageType,
      totalEntries,
      validEntries,
      expiredEntries,
    };
  }

  /**
   * Gets the size of cache data in bytes (approximate)
   */
  getCacheSize(storageType: StorageType = this.defaultStorage): number {
    const actualStorageType = this.getAvailableStorage(storageType);
    let totalSize = 0;

    try {
      if (actualStorageType === "memory") {
        // Approximate size calculation for memory cache
        for (const [key, entry] of this.memoryCache.entries()) {
          totalSize += key.length * 2; // Approximate character size
          totalSize += JSON.stringify(entry).length * 2;
        }
      } else {
        const storage = this.getStorage(actualStorageType);
        if (storage) {
          for (let i = 0; i < storage.length; i++) {
            const storageKey = storage.key(i);
            if (storageKey && storageKey.startsWith(this.CACHE_PREFIX)) {
              const stored = storage.getItem(storageKey);
              if (stored) {
                totalSize += storageKey.length * 2; // UTF-16 encoding
                totalSize += stored.length * 2;
              }
            }
          }
        }
      }
    } catch (error) {
      console.warn(`Error calculating ${actualStorageType} cache size:`, error);
    }

    return totalSize;
  }
}

// Global cache instance
const apiCache = new ApiCache();

// Cleanup expired entries every 5 minutes
if (typeof window !== "undefined") {
  setInterval(() => {
    apiCache.cleanup("localStorage");
    apiCache.cleanup("sessionStorage");
    apiCache.cleanup("memory");
  }, 5 * 60 * 1000);
}

const SAFE_HTTP_METHODS = new Set(["get", "head", "options"]);

/**
 * VAPT #15: auth is carried by HttpOnly cookies, so this no longer returns an
 * Authorization: Bearer header. For unsafe (state-changing) methods it returns
 * the CSRF double-submit header (`X-CSRFToken` echoing the readable `csrftoken`
 * cookie) that the backend requires on cookie-authenticated writes. For safe
 * methods it returns nothing. It never throws when unauthenticated - callers
 * that merge it into GET requests are unaffected (an extra header on GET is
 * harmless, and here safe methods get none).
 */
export const getAuthHeaders = (method?: string): Record<string, string> => {
  if (method && SAFE_HTTP_METHODS.has(method.toLowerCase())) return {};
  const csrf = readCookie("csrftoken");
  return csrf ? { "X-CSRFToken": csrf } : {};
};

/**
 * Flattens DRF field-level error bodies into a single readable string.
 *
 * DRF returns serializer errors as `{ field: ["msg1", "msg2"], other: [...] }`
 * or occasionally `{ field: "msg" }`. Nested dicts (e.g. nested serializers)
 * are walked one level deep. Lists of strings / non-field errors produced by
 * `non_field_errors` are also surfaced.
 *
 * Returns an empty string when nothing usable is found, so callers can fall
 * back to the HTTP-status message without a confusing "[object Object]".
 */
const extractDrfFieldErrors = (data: any): string => {
  if (data == null) return "";
  if (typeof data === "string") return data;
  if (Array.isArray(data)) {
    return data.map((v) => (typeof v === "string" ? v : JSON.stringify(v))).join("; ");
  }
  if (typeof data !== "object") return "";

  const parts: string[] = [];
  for (const [key, value] of Object.entries(data)) {
    if (value == null) continue;
    let rendered: string;
    if (Array.isArray(value)) {
      rendered = value
        .map((v) => (typeof v === "string" ? v : JSON.stringify(v)))
        .join(" ");
    } else if (typeof value === "string") {
      rendered = value;
    } else if (typeof value === "object") {
      rendered = extractDrfFieldErrors(value);
    } else {
      rendered = String(value);
    }
    if (!rendered) continue;
    parts.push(key === "non_field_errors" ? rendered : `${key}: ${rendered}`);
  }
  return parts.join("; ");
};

/**
 * Processes API errors and throws a more specific error message.
 * @param error The error object caught from an API call.
 * @param functionName The name of the function where the error occurred.
 * @returns Never, as it always throws an error.
 */
export const processApiError = (error: any, functionName: string): never => {
  console.error(`Error in ${functionName}:`, error);
  // Forward to New Relic with full message + stack (console.error only keeps
  // the label string in NR logs). `typeof window` guards SSR/RSC calls.
  if (typeof window !== "undefined") {
    window.newrelic?.noticeError(error, { context: functionName });
  }
  if (axios.isAxiosError(error) && error.response) {
    const data = error.response.data;
    const message =
      (data && typeof data === "object" && (data.detail || data.message || data.error)) ||
      extractDrfFieldErrors(data) ||
      `API request failed with status ${error.response.status}`;
    throw new Error(message);
  }
  throw error;
};

/**
 * Cached GET request with configurable cache duration and force refresh
 * @param url The URL to fetch
 * @param config Axios request configuration
 * @param cacheConfig Cache configuration options
 * @returns Promise resolving to the response data
 */
export const cachedGet = async <T = any>(
  url: string,
  config: AxiosRequestConfig = {},
  cacheConfig: CacheConfig = {}
): Promise<T> => {
  let { duration, force = false, key: customKey, storage, ignoreCache = false } = cacheConfig;

  // Never cache export requests (they contain export_format parameter)
  if (config.params?.export_format) {
    ignoreCache = true;
  }

  // Build full URL using helper function (used for policy matching)
  const fullUrl = buildApiUrl(url);

  // Determine base policy and overlay per-call overrides
  const _debug = resolvePolicyDebug(fullUrl, 'GET', 'axios');
  const basePolicy = _debug.policy;
  const overridePolicy = mergePolicyWithOverrides(basePolicy, {
    ttlMinutes: typeof duration === 'number' ? duration : undefined,
    storage: storage as any,
  });

  const cacheReadEnabled = isCacheEnabled() && overridePolicy.enabled && !force && !ignoreCache;
  const willCache = isCacheEnabled() && overridePolicy.enabled && !ignoreCache;

  // Always log in development or when debug flag is set
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test' || process.env.NEXT_PUBLIC_DEBUG_CACHE === 'true') {
    // eslint-disable-next-line no-console
    console.log('[CACHE][axios][DEBUG]', {
      url: fullUrl,
      method: 'GET',
      matchedPattern: _debug.matchedPattern || '(no match - using fallback)',
      basePolicy,
      effectivePolicy: overridePolicy,
      globallyEnabled: _debug.globallyEnabled,
      force,
      ignoreCache,
      cacheReadEnabled,
      willCache,
      reason: !_debug.globallyEnabled ? 'globally disabled' :
        !overridePolicy.enabled ? 'policy disabled' :
          force ? 'force refresh' :
            ignoreCache ? 'ignore cache' :
              'will use cache'
    });
  }

  // Generate cache key
  const cacheKey = customKey || apiCache["generateKey"](fullUrl, config.params, config.headers);

  // Check cache if enabled
  if (cacheReadEnabled) {
    const cachedData = apiCache.get<T>(cacheKey, overridePolicy.storage as any);
    if (cachedData !== null) {
      return cachedData;
    }
  }

  try {
    const response = await axiosInstance.get<T>(fullUrl, config);

    // Store only if caching is enabled and not an export request
    if (willCache) {
      apiCache.set(
        cacheKey,
        response.data,
        overridePolicy.ttlMinutes,
        overridePolicy.storage as any
      );
    }

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`[cachedGet] ${error.response?.status} ${fullUrl}`, {
        params: config.params,
        responseData: error.response?.data,
      });
      if (typeof window !== "undefined") {
        window.newrelic?.noticeError(error, {
          context: "cachedGet",
          status: error.response?.status ?? 0,
          url: fullUrl,
        });
      }
    }
    throw error;
  }
};

/**
 * Cached GET request with authentication headers
 * @param url The URL to fetch
 * @param config Axios request configuration
 * @param cacheConfig Cache configuration options
 * @returns Promise resolving to the response data
 */
export const cachedAuthGet = async <T = any>(
  url: string,
  config: AxiosRequestConfig = {},
  cacheConfig: CacheConfig = {}
): Promise<T> => {
  const authHeaders = getAuthHeaders("GET");
  const mergedConfig = {
    ...config,
    headers: {
      ...authHeaders,
      ...config.headers,
    },
  };

  return cachedGet<T>(url, mergedConfig, cacheConfig);
};

/**
 * Authenticated POST request
 * @param url The URL to post to
 * @param data The data to send
 * @param config Axios request configuration
 * @returns Promise resolving to the response data
 */
export const authPost = async <T = any>(
  url: string,
  data?: any,
  config: AxiosRequestConfig = {}
): Promise<T> => {
  try {
    const authHeaders = getAuthHeaders();
    const mergedConfig = {
      ...config,
      headers: {
        ...authHeaders,
        ...config.headers,
      },
    };

    // Build full URL using helper function
    const fullUrl = buildApiUrl(url);
    const response = await axiosInstance.post<T>(fullUrl, data, mergedConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Authenticated PUT request
 * @param url The URL to put to
 * @param data The data to send
 * @param config Axios request configuration
 * @returns Promise resolving to the response data
 */
export const authPut = async <T = any>(
  url: string,
  data?: any,
  config: AxiosRequestConfig = {}
): Promise<T> => {
  try {
    const authHeaders = getAuthHeaders();
    const mergedConfig = {
      ...config,
      headers: {
        ...authHeaders,
        ...config.headers,
      },
    };

    // Build full URL using helper function
    const fullUrl = buildApiUrl(url);
    const response = await axiosInstance.put<T>(fullUrl, data, mergedConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Authenticated DELETE request
 * @param url The URL to delete
 * @param config Axios request configuration
 * @returns Promise resolving to the response data
 */
export const authDelete = async <T = any>(
  url: string,
  config: AxiosRequestConfig = {}
): Promise<T> => {
  try {
    const authHeaders = getAuthHeaders();
    const mergedConfig = {
      ...config,
      headers: {
        ...authHeaders,
        ...config.headers,
      },
    };

    // Build full URL using helper function
    const fullUrl = buildApiUrl(url);
    const response = await axiosInstance.delete<T>(fullUrl, mergedConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Authenticated PATCH request
 * @param url The URL to patch
 * @param data The data to send
 * @param config Axios request configuration
 * @returns Promise resolving to the response data
 */
export const authPatch = async <T = any>(
  url: string,
  data?: any,
  config: AxiosRequestConfig = {}
): Promise<T> => {
  try {
    const authHeaders = getAuthHeaders();
    const mergedConfig = {
      ...config,
      headers: {
        ...authHeaders,
        ...config.headers,
      },
    };

    // Build full URL using helper function
    const fullUrl = buildApiUrl(url);
    const response = await axiosInstance.patch<T>(fullUrl, data, mergedConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Invalidates cache entries by pattern or specific key
 * @param pattern String pattern to match keys or specific key to remove
 * @param storageType Storage type to clear from (default: all)
 */
export const invalidateCache = (
  pattern: string,
  storageType?: StorageType
): void => {
  const storageTypes: StorageType[] = storageType
    ? [storageType]
    : ["localStorage", "sessionStorage", "memory"];

  for (const storage of storageTypes) {
    if (pattern === "*") {
      apiCache.clear(storage);
      //      console.log(`All cache entries cleared from ${storage}`);
      continue;
    }

    // For localStorage and sessionStorage, we need to iterate through keys
    if (storage !== "memory") {
      const storageInterface = apiCache["getStorage"](storage);
      if (storageInterface) {
        const keysToRemove: string[] = [];

        for (let i = 0; i < storageInterface.length; i++) {
          const storageKey = storageInterface.key(i);
          if (storageKey && storageKey.startsWith(apiCache["CACHE_PREFIX"])) {
            const actualKey = storageKey.substring(
              apiCache["CACHE_PREFIX"].length
            );
            if (actualKey.includes(pattern)) {
              keysToRemove.push(storageKey);
            }
          }
        }

        keysToRemove.forEach((key) => storageInterface.removeItem(key));
        //console.log(
        //  `${keysToRemove.length} cache entries deleted from ${storage} matching pattern: ${pattern}`
        //);
      }
    } else {
      // For memory cache, iterate through the Map
      let deletedCount = 0;
      for (const key of apiCache["memoryCache"].keys()) {
        if (key.includes(pattern)) {
          apiCache["memoryCache"].delete(key);
          deletedCount++;
        }
      }
      //console.log(
      //  `${deletedCount} cache entries deleted from memory matching pattern: ${pattern}`
      //);
    }
  }
};

/**
 * Gets cache statistics for debugging
 * @param storageType Storage type to get stats for (default: sessionStorage)
 */
export const getCacheStats = (storageType: StorageType = "sessionStorage") => {
  return apiCache.getStats(storageType);
};

/**
 * Gets the total size of cache data in bytes
 * @param storageType Storage type to get size for (default: sessionStorage)
 */
export const getCacheSize = (
  storageType: StorageType = "sessionStorage"
): number => {
  return apiCache.getCacheSize(storageType);
};

/**
 * Manually cleanup expired cache entries
 * @param storageType Storage type to cleanup (default: all)
 */
export const cleanupCache = (storageType?: StorageType): void => {
  if (storageType) {
    apiCache.cleanup(storageType);
  } else {
    apiCache.cleanup("localStorage");
    apiCache.cleanup("sessionStorage");
    apiCache.cleanup("memory");
  }
};

const MIME_TO_EXTENSION: Record<string, string> = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/bmp": "bmp",
  "image/svg+xml": "svg",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/tiff": "tiff",
  "text/csv": "csv",
  "text/plain": "txt",
  "application/json": "json",
  "application/zip": "zip",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/msword": "doc",
};

/**
 * Reconciles a requested filename against the blob's actual MIME type.
 *
 * Many call sites historically hardcoded ".pdf" even though the stored asset
 * may be a PNG/JPG snapshot. That produces files with a .pdf extension but
 * image bytes, which fail to open. This helper rewrites the extension to
 * match the blob's real type whenever we can detect a mismatch.
 *
 * @param filename The filename the caller requested.
 * @param blobType The blob's MIME type (e.g. "image/jpeg").
 * @returns The filename with a correct extension for the actual content.
 */
const reconcileFilenameWithBlobType = (
  filename: string,
  blobType: string | undefined
): string => {
  const trueExt = blobType ? MIME_TO_EXTENSION[blobType.toLowerCase()] : undefined;
  if (!trueExt) return filename;

  const extMatch = filename.match(/\.([a-z0-9]{2,5})$/i);
  const currentExt = extMatch?.[1]?.toLowerCase();

  if (!currentExt) {
    return `${filename}.${trueExt}`;
  }

  const equivalent =
    currentExt === trueExt ||
    (currentExt === "jpeg" && trueExt === "jpg") ||
    (currentExt === "jpg" && trueExt === "jpeg");

  if (equivalent) return filename;

  return filename.replace(/\.[a-z0-9]{2,5}$/i, `.${trueExt}`);
};

/**
 * Utility function to trigger a browser download for a Blob.
 *
 * The `filename` argument is treated as a hint: if the blob's MIME type
 * disagrees with the filename's extension, the extension is corrected
 * automatically. This prevents e.g. JPG snapshots being saved as .pdf.
 *
 * @param blob The Blob data to download.
 * @param filename The desired filename (extension may be auto-corrected).
 */
export const downloadFileFromBlob = (blob: Blob, filename: string): void => {
  const finalFilename = reconcileFilenameWithBlobType(filename, blob.type);

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = finalFilename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};



// Export the cache instance for advanced usage
export { apiCache };

/**
 * Generic API call function with configurable method and data
 * @param url The URL to call
 * @param config Request configuration including method, data, params
 * @returns Promise resolving to the response data
 */
export const apiCall = async <T = any>(
  url: string,
  config: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    data?: any;
    params?: any;
    headers?: Record<string, string>;
    cacheConfig?: CacheConfig;
  } = {}
): Promise<T> => {
  const {
    method = 'GET',
    data,
    params,
    headers = {},
    cacheConfig = {}
  } = config;

  try {
    const authHeaders = getAuthHeaders(method);
    const mergedHeaders = {
      ...authHeaders,
      'Content-Type': 'application/json',
      ...headers,
    };

    // Build full URL
    const fullUrl = buildApiUrl(url);

    if (method === 'GET') {
      // For GET requests, use cached version
      const axiosConfig = {
        headers: mergedHeaders,
        params
      };
      return await cachedGet<T>(fullUrl, axiosConfig, cacheConfig);
    } else {
      // For non-GET requests, use axios instance with interceptors
      const axiosConfig = {
        method: method.toLowerCase(),
        headers: mergedHeaders,
        data,
        params
      };

      const response = await axiosInstance(fullUrl, axiosConfig);
      return response.data;
    }
  } catch (error) {
    throw error;
  }
};

// --- User Presets API Functions ---

export interface UserPreset {
  id: string;
  user_id: string;
  page_url: string;
  preset_name: string;
  description?: string;
  preset_data: {
    columnFilters: any[];
    globalFilter?: string;
    columnVisibility?: Record<string, boolean>;
    sorting?: any[];
  };
  created_at: string;
  updated_at: string;
}

export interface SavePresetRequest {
  page_url: string;
  preset_name: string;
  description?: string;
  preset_data: {
    columnFilters: any[];
    globalFilter?: string;
    columnVisibility?: Record<string, boolean>;
    sorting?: any[];
  };
}

export interface PresetAPIResponse {
  success: boolean;
  preset?: UserPreset;
  presets?: UserPreset[];
  message?: string;
}

export interface PresetListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserPreset[];
}

/**
 * Save or update a user preset
 */
export async function saveUserPreset(data: SavePresetRequest): Promise<UserPreset> {
  return await apiCall<UserPreset>('/api/v1/user-presets/', {
    method: 'POST',
    data,
  });
}

/**
 * Get user presets for a specific page
 */
export async function getUserPresets(pageUrl: string, forceRefresh: boolean = false): Promise<PresetListResponse> {
  return await apiCall<PresetListResponse>('/api/v1/user-presets/', {
    method: 'GET',
    params: { page_url: pageUrl },
    cacheConfig: {
      force: forceRefresh,  // Force refresh to bypass cache
      duration: 5,  // Cache for 5 minutes
    },
  });
}

/**
 * Get a specific user preset by ID
 */
export async function getUserPreset(presetId: string): Promise<UserPreset> {
  return await apiCall<UserPreset>(`/api/v1/user-presets/${presetId}/`, {
    method: 'GET',
  });
}

/**
 * Delete a specific user preset
 */
export async function deleteUserPreset(presetId: string): Promise<void> {
  await apiCall<void>(`/api/v1/user-presets/${presetId}/`, {
    method: 'DELETE',
  });
}

/**
 * Get all user presets (admin function)
 */
export async function getAllUserPresets(): Promise<PresetAPIResponse> {
  return await apiCall<PresetAPIResponse>('/api/user-presets/all', {
    method: 'GET',
  });
}
